import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificationDatabaseService } from '../../services/notificationDatabaseService';
import { registerPwaServiceWorker } from '../../pwa/registerServiceWorker';
import DashboardHome from './DashboardHome';
import Bookings from './Bookings';
import Deliveries from './Deliveries';
import Payments from './Payments';
import Portfolio from './Portfolio';
import PortfolioManagement from '../portfolio-management';
import Availability from './Availability';
import Reviews from './Reviews';
import Profile from './Profile';
import Notifications from './Notifications';
import Support from './Support';
import MashhadLogo from '../../components/MashhadLogo';
import AppIcon from '../../components/AppIcon';

const PhotographerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const notifChannelRef = useRef(null);

  // SAFE MODE: PWA - Register SW only for photographer in production
  useEffect(() => {
    const isPhotographer = userProfile?.role === 'photographer';
    if (!isPhotographer) return;
    registerPwaServiceWorker(true).then(() => {});
  }, [userProfile?.role]);

  // تحميل عدد الإشعارات غير المقروءة
  useEffect(() => {
    if (!userProfile?.id) return;
    const load = async () => {
      try {
        const data = await notificationDatabaseService.getMyNotifications();
        setUnreadCount((data || []).filter((n) => !n.isRead).length);
      } catch {}
    };
    load();
    notifChannelRef.current = notificationDatabaseService.subscribeToNotifications(
      userProfile.id,
      (newNotif) => {
        if (!newNotif.isRead) setUnreadCount((prev) => prev + 1);
      }
    );
    return () => {
      if (notifChannelRef.current) {
        notificationDatabaseService.unsubscribeFromNotifications(notifChannelRef.current);
        notifChannelRef.current = null;
      }
    };
  }, [userProfile?.id]);

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Main Content - Full width, no sidebar */}
      <div className="min-h-screen flex flex-col">
        {/* Header - الشعار على اليمين، أيقونة الإشعارات على اليسار */}
        <header
          className="fixed inset-x-0 z-30 bg-white border-b border-slate-200 shadow-sm"
          style={{ top: 'var(--app-download-banner-height, 0px)', paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="px-4 md:px-6 py-3 flex items-center h-16">
            {/* يمين — placeholder بنفس عرض زر الجرس للتوازن */}
            <div className="w-10 flex-shrink-0" />

            {/* الشعار في المنتصف */}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex-1 flex items-center justify-center focus:outline-none hover:opacity-80 transition-opacity"
            >
              <MashhadLogo variant="dark" size="lg" logoType="header" />
            </button>

            {/* يسار — زر الإشعارات */}
            <button
              type="button"
              onClick={() => navigate('/photographer-dashboard/notifications')}
              className="relative w-10 flex-shrink-0 flex items-center justify-center rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              aria-label="الإشعارات"
            >
              <AppIcon name="Bell" size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6" style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top, 0px) + var(--app-download-banner-height, 0px))' }}>
          {/* زر الرجوع أعلى المحتوى */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 transition-colors mb-4 -mt-1"
            aria-label="رجوع"
          >
            <AppIcon name="ArrowRight" size={20} />
            <span className="text-sm font-medium">رجوع</span>
          </button>
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="bookings" element={<Bookings />} />
            <Route path="deliveries" element={<Deliveries />} />
            <Route path="payments" element={<Payments />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="portfolio-management" element={<PortfolioManagement />} />
            <Route path="availability" element={<Availability />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="profile" element={<Profile key="hub" />} />
            <Route path="profile/form" element={<Profile key="form" />} />
            <Route path="profile/service-area" element={<Profile key="service-area" />} />
            <Route path="profile/certificates" element={<Navigate to="/photographer-dashboard/profile/verification" replace />} />
            <Route path="profile/verification" element={<Profile key="verification" />} />
            <Route path="profile/settings" element={<Profile key="settings" />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="support" element={<Support />} />
            <Route path="*" element={<Navigate to="/photographer-dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default PhotographerDashboard;
